/**
 * Iter XX.75 — Practice with Gully (Phase 0 + Phase 1).
 *
 * Phase 1 replaces the raw file-picker path with an in-app recorder
 * (PracticeRecorder). The prep screen is now the entry point:
 *
 *   ┌──────────────────────────────────────────┐
 *   │  Practice with Gully                     │
 *   │  Let's work on your rhythm.              │
 *   │  • Place your phone side-on.             │
 *   │  • Keep your full run-up in frame.       │
 *   │  • Keep the phone steady.                │
 *   │  • Record one over.                      │
 *   │                                          │
 *   │  Gully records video and sound so it can │
 *   │  better understand your rhythm.          │
 *   │                                          │
 *   │  ▶ Start Recording   (primary)           │
 *   │  Upload existing video instead (link)    │
 *   └──────────────────────────────────────────┘
 *
 * Everything after "Use Video" (or a file pick) is identical to
 * Phase 0 — same /practice/upload → /practice/analyze pipeline,
 * same reply UI, same debug hooks.
 */
import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { X, Video, Loader2, CheckCircle2, AlertTriangle, Circle, Save } from "lucide-react";

import api, { API } from "../lib/api";
import { postClientEvent } from "../lib/clientEnv";
import { getToken } from "../lib/nativeTokenStorage";
import { webOnlyWithCredentials } from "../lib/nativePlatform";
import PracticeRecorder, { canUseInAppRecorder } from "./PracticeRecorder";

const FOCUS = "rhythm";
const INTENT = "practice";
const MAX_MB = 100;

const GUIDANCE = [
  "Place your phone side-on.",
  "Keep your full run-up in frame.",
  "Keep the phone steady.",
  "Record one over.",
];

function StageIcon({ stage }) {
  if (stage === "prep" || stage === "recording") {
    return <Video size={18} className="text-emerald-300" />;
  }
  if (stage === "uploading") {
    return <Loader2 size={18} className="text-emerald-300 animate-spin" />;
  }
  if (stage === "analysing") {
    return <Loader2 size={18} className="text-indigo-300 animate-spin" />;
  }
  if (stage === "done") {
    return <CheckCircle2 size={18} className="text-emerald-400" />;
  }
  if (stage === "error") {
    return <AlertTriangle size={18} className="text-amber-400" />;
  }
  return null;
}

function stageTitle(stage) {
  switch (stage) {
    case "prep":       return "Practice with Gully";
    case "recording":  return "Practice with Gully — Recording";
    case "uploading":  return "Practice with Gully — Sending";
    case "analysing":  return "Practice with Gully — Analysing";
    case "done":       return "Practice with Gully — Coaching read";
    case "error":      return "Practice with Gully — Something went wrong";
    default:           return "Practice with Gully";
  }
}

function postEvent(sessionId, name, meta) {
  // Reuses the shared /web/client-error sink (already parsed by the
  // backend as a client analytics event when `kind` doesn't match an
  // error class). Never blocks the flow.
  try {
    postClientEvent(API, sessionId || null, name, meta || {});
  } catch (_) { /* noop */ }
}

export function PracticeStarter({ onClose, sessionId }) {
  const fileRef = useRef(null);

  // stage machine
  //   prep → (recording | uploading via picker) → uploading → analysing → done | error
  const [stage, setStage] = useState("prep");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [reply, setReply] = useState("");
  const [structured, setStructured] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [practiceId, setPracticeId] = useState(null);

  // Blob held in memory so upload retries never require re-recording.
  const pendingBlobRef = useRef(null);
  const pendingMimeRef = useRef(null);
  const pendingFilenameRef = useRef("over.mp4");

  const recorderAvailable = canUseInAppRecorder();

  function trackEvent(name, meta) {
    postEvent(sessionId, name, meta);
  }

  // -------- Upload + Analyse (shared by recorder + picker paths) --------

  async function runUploadAndAnalyse(blob, mime) {
    setStage("uploading");
    setUploadProgress(0);
    setErrorMessage("");
    setReply("");
    setStructured(null);
    trackEvent("practice_upload_started", {
      size_bytes: blob.size, mime,
    });

    // ---- 1. Upload ----
    const form = new FormData();
    // Give the blob a filename so the server sees a real UploadFile.
    const filename = pendingFilenameRef.current || (mime?.includes("webm")
      ? "over.webm" : "over.mp4");
    const file = new File([blob], filename, { type: mime || blob.type });
    form.append("file", file);
    form.append("intent", INTENT);

    let assetId = null;
    try {
      const token = (() => {
        try { return getToken(); } catch { return null; }
      })();
      const res = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${API}/web/practice/upload`);
        xhr.withCredentials = webOnlyWithCredentials();
        if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable) {
            setUploadProgress(Math.round((ev.loaded / ev.total) * 100));
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try { resolve(JSON.parse(xhr.responseText || "{}")); }
            catch (err) { reject(err); }
          } else {
            reject(new Error(`upload_http_${xhr.status}`));
          }
        };
        xhr.onerror = () => reject(new Error("upload_network"));
        xhr.ontimeout = () => reject(new Error("upload_timeout"));
        xhr.timeout = 4 * 60 * 1000;
        xhr.send(form);
      });
      assetId = res?.practice_asset_id;
    } catch (err) {
      trackEvent("practice_upload_failed", {
        reason: String(err?.message || err),
      });
      setStage("error");
      setErrorMessage("Couldn't send your clip. Tap Retry to try again — we've kept your recording.");
      return;
    }

    if (!assetId) {
      setStage("error");
      setErrorMessage("Upload finished but the server didn't return an asset id. Tap Retry.");
      return;
    }
    trackEvent("practice_upload_completed", { size_bytes: blob.size });

    // ---- 2. Analyse ----
    setStage("analysing");
    try {
      const { data } = await api.post("/web/practice/analyze", {
        practice_asset_id: assetId,
        focus: FOCUS,
        intent: INTENT,
      });
      setReply(data?.reply || "");
      setStructured(data?.structured || null);
      setPracticeId(data?.practice_id || null);
      setStage("done");
      trackEvent("practice_analysis_completed", {
        practice_id: data?.practice_id,
        safe_to_cue: !!data?.safe_to_cue,
      });
      // Clear the retained blob — no reason to keep it after success.
      pendingBlobRef.current = null;
      pendingMimeRef.current = null;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      trackEvent("practice_analysis_failed", { detail: String(detail || "") });
      setStage("error");
      setErrorMessage(
        typeof detail === "string" && detail
          ? `Analysis failed (${detail}). Tap Retry.`
          : "Analysis failed. Tap Retry.",
      );
    }
  }

  // -------- Handlers --------

  function handleStartRecording() {
    trackEvent("practice_prep_start_recording");
    setStage("recording");
  }

  function handleUploadExistingClick() {
    trackEvent("practice_prep_upload_existing_clicked");
    fileRef.current?.click();
  }

  async function onFilePicked(e) {
    const file = e?.target?.files?.[0];
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`Clip is over ${MAX_MB}MB — try a shorter version.`);
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    pendingBlobRef.current = file;
    pendingMimeRef.current = file.type || "video/mp4";
    pendingFilenameRef.current = file.name || "over.mp4";
    trackEvent("practice_file_picked", { size_bytes: file.size });
    await runUploadAndAnalyse(file, pendingMimeRef.current);
    if (fileRef.current) fileRef.current.value = "";
  }

  async function handleRecorderComplete(blob, mime) {
    pendingBlobRef.current = blob;
    pendingMimeRef.current = mime;
    pendingFilenameRef.current = mime?.includes("webm") ? "over.webm" : "over.mp4";
    await runUploadAndAnalyse(blob, mime);
  }

  function handleRecorderCancel() {
    trackEvent("practice_recorder_dismissed");
    setStage("prep");
  }

  async function handleRetryFromError() {
    trackEvent("practice_retry_from_error");
    const blob = pendingBlobRef.current;
    const mime = pendingMimeRef.current;
    if (blob) {
      // Kept the blob — retry the network path only.
      await runUploadAndAnalyse(blob, mime);
    } else {
      // No blob to retry with — send them back to prep.
      setStage("prep");
      setErrorMessage("");
    }
  }

  function handleAnotherOver() {
    pendingBlobRef.current = null;
    pendingMimeRef.current = null;
    setReply("");
    setStructured(null);
    setPracticeId(null);
    setUploadProgress(0);
    setStage("prep");
    trackEvent("practice_another_over");
  }

  // Save the retained recording locally as a small safety net on
  // failure screens. Uses the in-memory blob only — no new network
  // request. The object URL is created and revoked in the same tick
  // because the browser has already started the download by then.
  function handleSaveRecording() {
    const blob = pendingBlobRef.current;
    const mime = pendingMimeRef.current || blob?.type || "";
    if (!blob) return;
    const ext = mime.includes("webm") ? "webm" : "mp4";
    const now = new Date();
    const p = (n) => String(n).padStart(2, "0");
    const stamp = `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}-${p(now.getHours())}${p(now.getMinutes())}`;
    const filename = `gully-practice-${stamp}.${ext}`;

    trackEvent("practice_save_recording_local", {
      size_bytes: blob.size, ext,
    });

    let url = null;
    try {
      url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      // Some mobile browsers require the anchor to be in the DOM.
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success("Saved to your device.");
    } catch (_) {
      toast.error("Couldn't save the file. Try again from Retry send.");
    } finally {
      // Revoke shortly after — long enough for the browser to have
      // grabbed the resource, short enough to free memory quickly.
      if (url) {
        setTimeout(() => {
          try { URL.revokeObjectURL(url); } catch (_) { /* noop */ }
        }, 60_000);
      }
    }
  }

  function handleRetakeFromError() {
    // Discard the retained blob and go back to the prep screen so
    // the founder can start fresh (or open the recorder again).
    pendingBlobRef.current = null;
    pendingMimeRef.current = null;
    setErrorMessage("");
    setStage("prep");
    trackEvent("practice_retake_from_error");
  }

  // -------- Render --------

  // Full-screen recorder replaces the modal chrome while active.
  if (stage === "recording") {
    return (
      <PracticeRecorder
        onCancel={handleRecorderCancel}
        onComplete={handleRecorderComplete}
        onEvent={trackEvent}
      />
    );
  }

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-2 sm:p-4"
      data-testid="practice-starter-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="w-full max-w-lg rounded-2xl bg-neutral-950 border border-neutral-800 shadow-2xl overflow-hidden max-h-[100dvh] sm:max-h-[90dvh] flex flex-col"
        style={{
          // Iter XX.287 — Reserve nav clearance via the shared
          // `--gully-bottom-nav-h` token; falls back to safe-area
          // inset on surfaces without the nav.
          paddingBottom: "var(--gully-bottom-nav-h, env(safe-area-inset-bottom, 0px))",
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-800">
          <div className="flex items-center gap-2 min-w-0">
            <StageIcon stage={stage} />
            <div className="text-sm font-medium text-neutral-100 truncate">
              {stageTitle(stage)}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-neutral-800 text-neutral-400 hover:text-neutral-200 flex-shrink-0"
            data-testid="practice-starter-close"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-4 space-y-4">
          {stage === "prep" && (
            <div
              className="space-y-3"
              data-testid="practice-starter-prep"
            >
              <div className="text-neutral-200 text-[15px] leading-relaxed">
                Let&apos;s work on your <em className="text-emerald-300 not-italic">rhythm</em>.
              </div>
              <ul className="space-y-1.5 text-[13px] text-neutral-300">
                {GUIDANCE.map((g) => (
                  <li key={g} className="flex items-start gap-2">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                    <span>{g}</span>
                  </li>
                ))}
              </ul>
              <div
                className="rounded-lg bg-neutral-900/70 border border-neutral-800 px-3 py-2 text-[12px] text-neutral-400 leading-relaxed"
                data-testid="practice-starter-permission-note"
              >
                Gully records video and sound so it can better understand your rhythm.
              </div>

              <button
                onClick={
                  recorderAvailable ? handleStartRecording : handleUploadExistingClick
                }
                className="w-full rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold py-3 flex items-center justify-center gap-2 transition-colors"
                data-testid="practice-starter-start-recording"
              >
                <Circle size={16} className="fill-white" />
                Start Recording
              </button>

              <div className="text-center">
                <button
                  onClick={handleUploadExistingClick}
                  className="text-[12px] text-neutral-500 hover:text-neutral-300 underline underline-offset-4 decoration-neutral-700 hover:decoration-neutral-500 transition-colors"
                  data-testid="practice-starter-upload-existing"
                >
                  Upload existing video instead
                </button>
              </div>
            </div>
          )}

          {stage === "uploading" && (
            <div
              className="space-y-2"
              data-testid="practice-starter-uploading"
            >
              <div className="text-[13px] text-neutral-300">
                Sending your over… {uploadProgress}%
              </div>
              <div className="h-2 w-full rounded-full bg-neutral-900 overflow-hidden">
                <div
                  className="h-full bg-emerald-500 transition-all"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          {stage === "analysing" && (
            <div
              className="flex items-center gap-3 py-3"
              data-testid="practice-starter-analysing"
            >
              <Loader2 size={20} className="text-indigo-300 animate-spin flex-shrink-0" />
              <div className="text-[14px] text-neutral-200 leading-relaxed">
                Gully is watching your over…
              </div>
            </div>
          )}

          {stage === "done" && (
            <div
              className="space-y-3"
              data-testid="practice-starter-done"
            >
              <div
                className="rounded-xl bg-neutral-900/70 border border-neutral-800 px-3 py-3 text-[14px] leading-relaxed text-neutral-100 whitespace-pre-wrap"
                data-testid="practice-starter-reply"
              >
                {reply || "(no reply)"}
              </div>
              {structured && (
                <div
                  className="text-[11px] text-neutral-500"
                  data-testid="practice-starter-meta"
                >
                  video_quality: {String(structured.video_quality || "?")} ·
                  confidence: {String(structured.confidence ?? "?")} ·
                  observed: {String(structured.clearly_observed_deliveries ?? "?")}
                  {structured.safe_to_cue === false && " · cue withheld"}
                </div>
              )}
              <div className="flex gap-2">
                <button
                  onClick={handleAnotherOver}
                  className="flex-1 rounded-lg bg-neutral-800 hover:bg-neutral-700 text-neutral-100 text-sm py-2 transition-colors"
                  data-testid="practice-starter-again"
                >
                  Try another over
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm py-2 transition-colors"
                  data-testid="practice-starter-close-done"
                >
                  Done
                </button>
              </div>
              {practiceId && (
                <div className="text-[10px] text-neutral-600 text-center">
                  practice_id: {practiceId}
                </div>
              )}
            </div>
          )}

          {stage === "error" && (
            <div
              className="space-y-3"
              data-testid="practice-starter-error"
            >
              <div className="rounded-xl bg-amber-950/40 border border-amber-800/60 px-3 py-2 text-[13px] text-amber-100">
                {errorMessage || "Something went wrong."}
              </div>
              {pendingBlobRef.current ? (
                // Retained-blob path: three-tier hierarchy so the
                // founder can never lose a real recording.
                //   Primary   → Retry send
                //   Secondary → Save recording (local, no upload)
                //   Tertiary  → Retake
                <div className="flex flex-col gap-2">
                  <button
                    onClick={handleRetryFromError}
                    className="w-full rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold py-2.5 transition-colors"
                    data-testid="practice-starter-retry"
                  >
                    Retry send
                  </button>
                  <button
                    onClick={handleSaveRecording}
                    className="w-full rounded-lg bg-neutral-800 hover:bg-neutral-700 text-neutral-100 text-sm py-2 flex items-center justify-center gap-2 transition-colors"
                    data-testid="practice-starter-save-recording"
                  >
                    <Save size={14} />
                    Save recording
                  </button>
                  <button
                    onClick={handleRetakeFromError}
                    className="w-full text-[12px] text-neutral-500 hover:text-neutral-300 underline underline-offset-4 decoration-neutral-700 hover:decoration-neutral-500 py-1 transition-colors"
                    data-testid="practice-starter-retake"
                  >
                    Retake
                  </button>
                </div>
              ) : (
                // No blob to save — simpler two-button layout.
                <div className="flex gap-2">
                  <button
                    onClick={() => setStage("prep")}
                    className="flex-1 rounded-lg bg-neutral-800 hover:bg-neutral-700 text-neutral-100 text-sm py-2 transition-colors"
                    data-testid="practice-starter-back-to-prep"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleRetryFromError}
                    className="flex-1 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm py-2 transition-colors"
                    data-testid="practice-starter-retry"
                  >
                    Try again
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Hidden picker — used by the secondary "Upload existing" link
            AND as the graceful fallback when the in-app recorder is
            unavailable. On iOS, capture="environment" opens the native
            camera app directly (no gallery). */}
        <input
          ref={fileRef}
          type="file"
          accept="video/*"
          capture="environment"
          className="hidden"
          onChange={onFilePicked}
          data-testid="practice-starter-file-input"
        />
      </div>
    </div>
  );
}
